import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
// form
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// material
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Stack,
  Autocomplete,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  Box,
  // Badge, // Removed unused import
  InputAdornment,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// import { styled } from '@mui/material/styles'; // Removed unused import
// redux
import { useDispatch, useSelector } from 'react-redux';
import { upsertConversation, updateUserPresence } from '../../redux/slices/internalChat';
// internal
import axios from '../../utils/axios';
import { FormProvider, RHFTextField, RHFSelect } from '../../components/hook-form';
import Iconify from '../../components/Iconify';
import UserStatusIndicator from './UserStatusIndicator';
import { HOST_API } from '../../config';
import useAuth from '../../hooks/useAuth';

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

GroupModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectChat: PropTypes.func,
};

export default function GroupModal({ open, onClose, onSelectChat }) {
  const { user } = useAuth();
  const dispatch = useDispatch();

  // Toggle between "New Chat" (User List) and "New Group" (Form)
  const [view, setView] = useState('list'); // 'list' | 'group'
  const [searchTerm, setSearchTerm] = useState('');

  const [allUsers, setAllUsers] = useState([]);
  const userStatusById = useSelector((state) => state.internalChat.presence.userStatusById);

  useEffect(() => {
    if (open) {
      axios
        .get('/api/user/contacts')
        .then((response) => {
          const users = response.data.users || [];
          setAllUsers(users.filter((u) => u._id !== user._id));

          // Sync presence to Redux
          users.forEach((u) => {
            dispatch(
              updateUserPresence({
                userId: u._id,
                online: u.status === 'online',
                lastSeenAt: u.lastSeenAt,
              })
            );
          });
        })
        .catch((err) => console.error(err));

      setView('list');
      setSearchTerm('');
    }
  }, [open, user._id, dispatch]);

  const NewGroupSchema = Yup.object().shape({
    name: Yup.string().required('Nome do grupo é obrigatório'),
    participants: Yup.array().min(1, 'Selecione pelo menos 1 participante'),
    mode: Yup.string().oneOf(['normal', 'announcement', 'mailbox']),
  });

  const defaultValues = {
    name: '',
    participants: [],
    mode: 'normal',
  };

  const methods = useForm({
    resolver: yupResolver(NewGroupSchema),
    defaultValues,
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmitGroup = async (values) => {
    try {
      const response = await axios.post('/api/internal-chat', {
        type: 'group',
        name: values.name,
        mode: values.mode,
        participantIds: values.participants.map((u) => u._id),
      });

      dispatch(upsertConversation(response.data));
      reset();
      onClose();

      if (onSelectChat && response.data?._id) {
        onSelectChat(response.data._id);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleStartDirectChat = async (targetUser) => {
    try {
      const response = await axios.post('/api/internal-chat', {
        type: 'private',
        name: targetUser.name,
        participantIds: [targetUser._id],
      });
      dispatch(upsertConversation(response.data));
      onClose();
      // Reload to show new chat or rely on socket?
      // The user asked about "New Chat" list, so I assume this is just for starting.
      // Let's keep existing behavior but fixing the data source.

      if (onSelectChat && response.data?._id) {
        onSelectChat(response.data._id);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getUserStatus = (user) => (userStatusById[user._id]?.online ? 'online' : 'offline');

  const filteredUsers = allUsers.filter((u) => u.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {view === 'list' ? 'Nova Conversa' : 'Novo Grupo'}
        {view === 'list' && (
          <Button size="small" startIcon={<Iconify icon="eva:people-fill" />} onClick={() => setView('group')}>
            Criar Grupo
          </Button>
        )}
        {view === 'group' && (
          <Button size="small" startIcon={<Iconify icon="eva:arrow-back-fill" />} onClick={() => setView('list')}>
            Voltar
          </Button>
        )}
      </DialogTitle>

      {view === 'list' && (
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Buscar usuário..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            margin="dense"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          <List sx={{ mt: 1 }}>
            {filteredUsers.map((user) => (
              <ListItem button key={user._id} onClick={() => handleStartDirectChat(user)} sx={{ borderRadius: 1 }}>
                <ListItemAvatar>
                  <UserStatusIndicator status={getUserStatus(user)}>
                    <Avatar alt={user.name} src={user.avatar ? `${HOST_API}${user.avatar}` : ''}>
                      {user.name.charAt(0).toUpperCase()}
                    </Avatar>
                  </UserStatusIndicator>
                </ListItemAvatar>
                <ListItemText
                  primary={user.name}
                  secondary={user.email}
                  primaryTypographyProps={{ variant: 'subtitle2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                {user.isCrossCompany && (
                  <Chip
                    size="small"
                    label={user.externalCompanyName}
                    color="info"
                    variant="filled"
                    sx={{ mr: 1, fontSize: '0.65rem' }}
                  />
                )}
                <Box>
                  <Chip
                    size="small"
                    label={getUserStatus(user) === 'online' ? 'Online' : 'Offline'}
                    color={getUserStatus(user) === 'online' ? 'success' : 'default'}
                    variant="outlined"
                  />
                </Box>
              </ListItem>
            ))}
            {filteredUsers.length === 0 && (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                Nenhum usuário encontrado.
              </Typography>
            )}
          </List>
        </DialogContent>
      )}

      {view === 'group' && (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmitGroup)}>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <RHFTextField name="name" label="Nome do Grupo" />

              <RHFSelect name="mode" label="Tipo de Conversa">
                <option value="normal">Grupo Normal</option>
                <option value="announcement">Mural Coletivo (Somente Leitura)</option>
                <option value="mailbox">Caixa Postal (Somente Leitura)</option>
              </RHFSelect>

              <Controller
                name="participants"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    multiple
                    freeSolo
                    onChange={(event, newValue) => field.onChange(newValue)}
                    options={allUsers}
                    getOptionLabel={(option) => option.name}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip {...getTagProps({ index })} key={option._id} size="small" label={option.name} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField label="Participantes" {...params} error={!!error} helperText={error?.message} />
                    )}
                  />
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="inherit">
              Cancelar
            </Button>
            <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
              Criar Grupo
            </LoadingButton>
          </DialogActions>
        </FormProvider>
      )}
    </Dialog>
  );
}
